import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, GraduationCap, Users } from "lucide-react";

function monthNow() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ReportsPage() {
  const { t, lang } = useI18n();
  const [month, setMonth] = useState(monthNow());
  const [busy, setBusy] = useState(null);

  const download = async (kind) => {
    setBusy(kind);
    try {
      const res = await api.get(`/reports/attendance/${kind}.csv`, {
        params: { month },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}-attendance-${month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const text = err.response?.data ? await err.response.data.text?.() : "";
      let detail = text;
      try { detail = JSON.parse(text).detail; } catch { /* ignore */ }
      toast.error(formatApiErrorDetail(detail) || "Download failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("reports")}</h1>
        <p className="text-sm text-muted-foreground">
          {lang === "ms" ? "Muat turun rekod kehadiran bulanan sebagai CSV." : "Download monthly attendance records as CSV."}
        </p>
      </div>

      <Card className="rounded-2xl max-w-md">
        <CardContent className="p-6 space-y-3">
          <Label>{t("month")}</Label>
          <Input
            data-testid="report-month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-12 num text-lg"
          />
          <p className="num text-xs text-muted-foreground">{month}</p>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="rounded-2xl"><CardContent className="p-6">
          <div className="mb-3 grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary">
            <GraduationCap className="h-6 w-6" />
          </div>
          <h2 className="font-display text-lg font-bold">{t("students")} {t("attendance")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {lang === "ms" ? "Semua tanda hadir/lewat/tidak hadir untuk bulan ini." : "All present/late/absent marks for the month."}
          </p>
          <Button
            onClick={() => download("students")}
            disabled={busy === "students"}
            data-testid="dl-students-csv"
            className="mt-4 rounded-full h-11 gap-2"
          >
            <Download className="h-4 w-4" /> {t("downloadCsv")}
          </Button>
        </CardContent></Card>

        <Card className="rounded-2xl"><CardContent className="p-6">
          <div className="mb-3 grid h-12 w-12 place-items-center rounded-xl bg-secondary/10 text-secondary">
            <Users className="h-6 w-6" />
          </div>
          <h2 className="font-display text-lg font-bold">{t("staff")} & {t("teacher")} {t("attendance")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {lang === "ms" ? "Rekod daftar masuk/keluar staf dan guru." : "Staff and teacher check-in / check-out records."}
          </p>
          <Button
            onClick={() => download("staff")}
            disabled={busy === "staff"}
            data-testid="dl-staff-csv"
            className="mt-4 rounded-full h-11 gap-2"
          >
            <Download className="h-4 w-4" /> {t("downloadCsv")}
          </Button>
        </CardContent></Card>
      </div>
    </div>
  );
}
